import React, { useEffect, useState, useRef } from "react";
import { useLocation, Link } from "react-router-dom";
import { useAuth } from "../contexts/AuthContext";
import { AGENT_NAME } from "../config";
import traderbroLogo from "../assets/traderbro-logo.svg";
import traderbroFavicon from "../assets/TraderBro-Fabicon.png";
import aaplChart1 from "../assets/AAPL_NASDAQ_1y_1d_basic.jpeg";
import aaplChart2 from "../assets/AAPL_NASDAQ_1y_1d_candlestick.jpeg";
import LiveActivityFeed from "../components/LiveActivityFeed";
import CoreFeatures from "../components/CoreFeatures";
import AboutUs from "../components/AboutUs";
import PricingPage from "../components/Pricing";
import Footer from "../components/Footer";

const LandingPage = () => {
  const { login, isAuthenticated } = useAuth();
  const [loginError, setLoginError] = useState("");
  const location = useLocation();
  const [messageIndex, setMessageIndex] = useState(0);

  // Handle hash scrolling on load
  useEffect(() => {
    if (location.hash) {
      const id = location.hash.replace("#", "");
      setTimeout(() => {
        const element = document.getElementById(id);
        if (element) {
          element.scrollIntoView({ behavior: "smooth" });
        }
      }, 500);
    }
  }, [location]);
  const [isTyping, setIsTyping] = useState(false);
  const [expandedMessages, setExpandedMessages] = useState({});
  const chatContainerRef = useRef(null);

  // WhatsApp conversation demo data
  const conversation = [
    { type: "user", text: "Hi", delay: 500 },
    {
      type: "bot",
      text: "Hi there! 👋\n\nI'm your AI stock analysis assistant, ready to help you with comprehensive stock market research and analysis!\n\n🎯 What I can do for you:\n• Generate detailed stock charts\n• Screen stocks based on your criteria\n• Compare multiple stocks side-by-side\n• Provide real-time market insights",
      delay: 1500,
    },
    { type: "user", text: "Show me AAPL chart", delay: 2000 },
    {
      type: "bot",
      delay: 2500,
      collapsible: true,
      preview:
        "*AAPL (NASDAQ) 1-Year Technical Analysis*\n\n---\n\n*1. Price Patterns*\n\n• The chart shows a significant dip in early spring, followed by a strong recovery and a sharp rally into late summer/early fall.\n• After the initial drop, price action formed a base and then broke out, with higher highs and higher lows evident in the last quarter.",
      fullText:
        "*AAPL (NASDAQ) 1-Year Technical Analysis*\n\n---\n\n*1. Price Patterns*\n\n• The chart shows a significant dip in early spring, followed by a strong recovery and a sharp rally into late summer/early fall.\n• After the initial drop, price action formed a base and then broke out, with higher highs and higher lows evident in the last quarter.\n\n*2. Key Support and Resistance Levels*\n\n• *Support:*\n  - Around $200: This level acted as a floor after the spring sell-off and was tested multiple times.\n  - Around $220: Minor support, previously a resistance level that turned into support during the recovery.\n• *Resistance:*\n  - Around $240: Previous highs and consolidation area before the recent breakout.\n  - Around $260: Recent peak and potential resistance as price approaches this level.\n\n*3. Overall Trend Direction*\n\n• *Bullish:*\n  - The overall trend has shifted from sideways/bearish (early year) to bullish (recent months), with price breaking above previous resistance and moving strongly higher.\n  - The price is above the 20-period Bollinger Band midline, confirming upward momentum.\n\n*4. Notable Chart Patterns*\n\n• *V-shaped Reversal:*\n  - The sharp drop and equally sharp recovery in spring suggest a classic V-shaped reversal.\n• *Breakout:*\n  - Recent price action shows a breakout above previous resistance ($240), indicating renewed bullish momentum.\n• *Potential Continuation:*\n  - The rally after consolidation hints at a continuation pattern, with momentum favoring further upside.\n\n*5. Volume Insights*\n\n• Volume is not directly visible on these charts, but the strength and speed of the recent rally suggest increased buying interest.\n• RSI spikes above 70 during rallies indicate strong momentum, often accompanied by higher volume.\n\n*6. RSI Insights*\n\n• RSI has moved into overbought territory (>70) several times during the rally, suggesting strong momentum but also caution for potential short-term pullbacks.\n• Previous dips into oversold (<30) coincided with the spring bottom, marking a reversal point.\n\n*7. Bollinger Bands*\n\n• Price has broken above the upper Bollinger Band in the recent rally, indicating strong bullish momentum but also potential for short-term mean reversion or consolidation.\n\n---\n\n*Overall Assessment & What Traders Should Watch For*\n\n• *Trend:* Bullish, with strong upward momentum and recent breakout above key resistance.\n• *Actionable Levels:* Watch for support at $240 and $220 on any pullbacks. Resistance at $260 is the next hurdle.\n• *Momentum:* RSI is elevated; short-term traders should be alert for overbought conditions and possible pullbacks.\n• *Pattern:* The V-shaped recovery and breakout suggest further upside potential, but after such a strong move, consolidation or a retest of breakout levels ($240) is possible.\n• *Watch for:*\n  - Sustained price above $240 to confirm the breakout.\n  - Any reversal signals (bearish candlesticks, RSI divergence) near $260 resistance.\n  - Continuation of higher highs and higher lows for trend confirmation.\n\n*Summary:* AAPL has transitioned to a bullish trend with a strong breakout and momentum. Traders should watch for consolidation near resistance and use pullbacks to support levels as potential entry points. Overbought RSI warrants caution for short-term reversals, but the overall technical picture favors further upside if key support holds.",
    },
    { type: "bot", image: aaplChart1, delay: 1000 },
    { type: "bot", image: aaplChart2, delay: 1000 },
    { type: "user", text: "What is RSI?", delay: 1500 },
    {
      type: "bot",
      text: 'Great question! 📈\n\nRSI (Relative Strength Index) measures momentum on a 0-100 scale:\n\n• Above 70 = Overbought (might decline)\n• Below 30 = Oversold (might rebound)\n\nIt helps identify if a stock has moved too far, too fast. Think of it as a "temperature gauge" for stock momentum!',
      delay: 2000,
    },
  ];

  const toggleExpand = (index) => {
    setExpandedMessages((prev) => ({
      ...prev,
      [index]: !prev[index],
    }));
  };

  useEffect(() => {
    if (messageIndex < conversation.length) {
      const timer = setTimeout(() => {
        setIsTyping(true);
        setTimeout(() => {
          setIsTyping(false);
          setMessageIndex((prev) => prev + 1);
        }, 800);
      }, conversation[messageIndex].delay);

      return () => clearTimeout(timer);
    }
  }, [messageIndex]);

  // Auto-scroll to bottom when new messages appear
  useEffect(() => {
    if (chatContainerRef.current) {
      chatContainerRef.current.scrollTo({
        top: chatContainerRef.current.scrollHeight,
        behavior: "smooth",
      });
    }
  }, [messageIndex, isTyping]);

  if (isAuthenticated) {
    return (
      <div className="min-h-screen flex items-center justify-center bg-gradient-to-br from-white via-gray-50 to-gray-100 relative overflow-hidden">
        {/* Animated Background */}
        <div className="absolute inset-0">
          <div className="absolute w-[600px] h-[600px] bg-gradient-to-br from-brand-cta/8 via-emerald-500/5 to-transparent rounded-full blur-3xl -top-48 -right-48 animate-float"></div>
          <div className="absolute w-[500px] h-[500px] bg-gradient-to-tl from-brand-cta/6 via-teal-500/4 to-transparent rounded-full blur-3xl top-1/2 -left-32 animate-float-delayed"></div>
        </div>

        <div className="text-center relative z-10">
          <div className="text-brand-cta text-6xl mb-4 animate-bounce">✓</div>
          <div className="text-brand-dark text-2xl font-bold mb-2">
            Already logged in!
          </div>
          <div className="text-gray-600">Redirecting to dashboard...</div>
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-gradient-to-br from-white via-gray-50 to-gray-100 relative overflow-x-hidden">
      {/* Vibrant Animated Background */}
      <div className="absolute inset-0 pointer-events-none z-0 overflow-hidden">
        {/* Large animated gradient orbs */}
        <div className="absolute w-[600px] h-[600px] bg-gradient-to-br from-brand-cta/8 via-emerald-500/5 to-transparent rounded-full blur-3xl -top-48 -right-48 animate-float"></div>
        <div className="absolute w-[500px] h-[500px] bg-gradient-to-tl from-brand-cta/6 via-teal-500/4 to-transparent rounded-full blur-3xl top-1/2 -left-32 animate-float-delayed"></div>
        <div
          className="absolute w-[400px] h-[400px] bg-gradient-to-br from-green-500/5 via-brand-cta/5 to-transparent rounded-full blur-3xl bottom-0 right-1/3 animate-float"
          style={{ animationDelay: "1s" }}
        ></div>

        {/* Subtle noise texture overlay */}
        <div
          className="absolute inset-0 opacity-20"
          style={{
            backgroundImage: `url("data:image/svg+xml,%3Csvg viewBox='0 0 400 400' xmlns='http://www.w3.org/2000/svg'%3E%3Cfilter id='noiseFilter'%3E%3CfeTurbulence type='fractalNoise' baseFrequency='0.9' numOctaves='4' /%3E%3C/filter%3E%3Crect width='100%25' height='100%25' filter='url(%23noiseFilter)' opacity='0.03'/%3E%3C/svg%3E")`,
          }}
        ></div>
      </div>

      {/* Navigation Menu */}
      <nav className="sticky top-0 z-50 bg-white/95 backdrop-blur-lg border-b border-gray-100 shadow-sm">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="flex items-center justify-between h-20">
            <div className="flex items-center gap-2">
              <img src={traderbroLogo} alt="TraderBro" className="h-9 w-auto" />
            </div>
            <div className="flex items-center gap-8">
              <a
                href="#live-activity"
                onClick={(e) => {
                  e.preventDefault();
                  document
                    .getElementById("live-activity")
                    ?.scrollIntoView({ behavior: "smooth" });
                }}
                className="text-gray-600 hover:text-[#222425] font-medium text-sm tracking-wide transition-colors duration-200"
              >
                Activity
              </a>
              <a
                href="#features"
                onClick={(e) => {
                  e.preventDefault();
                  document
                    .getElementById("features")
                    ?.scrollIntoView({ behavior: "smooth" });
                }}
                className="text-gray-600 hover:text-[#222425] font-medium text-sm tracking-wide transition-colors duration-200"
              >
                Features
              </a>
              <a
                href="#about"
                onClick={(e) => {
                  e.preventDefault();
                  document
                    .getElementById("about")
                    ?.scrollIntoView({ behavior: "smooth" });
                }}
                className="text-gray-600 hover:text-[#222425] font-medium text-sm tracking-wide transition-colors duration-200"
              >
                About
              </a>
              <a
                href="#pricing"
                onClick={(e) => {
                  e.preventDefault();
                  document
                    .getElementById("pricing")
                    ?.scrollIntoView({ behavior: "smooth" });
                }}
                className="text-gray-600 hover:text-[#222425] font-medium text-sm tracking-wide transition-colors duration-200"
              >
                Pricing
              </a>
              <Link
                to="/research"
                className="text-gray-600 hover:text-[#222425] font-medium text-sm tracking-wide transition-colors duration-200"
              >
                Research
              </Link>
              <a
                href="/login"
                className="bg-[#222425] hover:bg-[#00DA76] text-white font-medium text-sm px-6 py-2.5 rounded-lg transition-all duration-300 shadow-sm hover:shadow-md"
              >
                Log In
              </a>
            </div>
          </div>
        </div>
      </nav>

      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 relative z-10">
        <div className="grid grid-cols-1 lg:grid-cols-2 gap-16 items-center min-h-screen py-16">
          {/* Left Side - Content */}
          <div className="relative text-center lg:text-left">
            {/* Logo and Title */}
            <div className="mb-8">
              <div className="flex items-center justify-center lg:justify-start gap-4 mb-6">
                <img
                  src={traderbroLogo}
                  alt="TraderBro"
                  className="h-16 w-auto"
                />
              </div>
              <h1 className="text-5xl sm:text-6xl md:text-7xl lg:text-8xl font-black leading-[1.08] tracking-tight mb-8">
                <span className="block text-[#222425] mb-2">Your AI</span>
                <span className="block text-[#222425] mb-2">Trading</span>
                <span className="block text-[#222425] mb-2">Assistant</span>
                <span className="block mt-4 animate-shimmer">
                  Starts Here
                </span>
              </h1>

              <p className="text-lg md:text-xl text-gray-600 leading-relaxed max-w-xl mx-auto lg:mx-0 mb-10 font-light">
                The Autonomous Financial Intelligence Platform.
                Institutional-grade analysis engine with real-time delivery via Web and Mobile.
              </p>
            </div>

            {/* Login Section */}
            <div className="max-w-md mx-auto lg:mx-0">
              <div className="bg-white/90 backdrop-blur-md rounded-3xl p-8 shadow-2xl border border-gray-100">
                <div className="space-y-6">
                  {loginError && (
                    <div className="bg-red-50 border border-red-200 text-red-700 px-4 py-3 rounded-md text-sm">
                      {loginError}
                    </div>
                  )}

                  <div className="space-y-6">
                    <div className="text-center">
                      <p className="text-sm text-gray-600 mb-6 font-medium">
                        Access your intelligent trading platform
                      </p>
                      <Link
                        to="/login"
                        onClick={() => {
                          if (typeof gtag !== "undefined") {
                            gtag("event", "click", {
                              event_category: "dashboard_cta",
                              event_label: "main_cta",
                              value: 1,
                            });
                          }
                        }}
                        className="group bg-[#222425] hover:bg-[#00DA76] text-white font-semibold text-base px-8 py-4 rounded-xl transition-all duration-300 shadow-lg hover:shadow-2xl flex items-center justify-center gap-2.5 hover:gap-3 w-full"
                      >
                        <svg
                          className="w-5 h-5 text-white transition-colors duration-300"
                          fill="none"
                          stroke="currentColor"
                          viewBox="0 0 24 24"
                        >
                          <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 19v-6a2 2 0 00-2-2H5a2 2 0 00-2 2v6a2 2 0 002 2h2a2 2 0 002-2zm0 0V9a2 2 0 012-2h2a2 2 0 012 2v10m-6 0a2 2 0 002 2h2a2 2 0 002-2m0 0V5a2 2 0 012-2h2a2 2 0 012 2v14a2 2 0 01-2 2h-2a2 2 0 01-2-2z" />
                        </svg>
                        <span>Launch Web Dashboard</span>
                        <svg
                          className="w-4 h-4 text-white group-hover:translate-x-0.5 transition-all duration-300"
                          fill="none"
                          stroke="currentColor"
                          viewBox="0 0 24 24"
                        >
                          <path
                            strokeLinecap="round"
                            strokeLinejoin="round"
                            strokeWidth={2.5}
                            d="M13 7l5 5m0 0l-5 5m5-5H6"
                          />
                        </svg>
                      </Link>
                    </div>

                    <div className="text-center">
                      <a
                        href="https://t.me/traderbro_cli_bot"
                        target="_blank"
                        rel="noopener noreferrer"
                        onClick={() => {
                          if (typeof gtag !== "undefined") {
                            gtag("event", "click", {
                              event_category: "cli_cta",
                              event_label: "hero_telegram_button",
                              value: 1,
                            });
                          }
                        }}
                        className="group border border-[#00DA76] text-[#00DA76] hover:bg-[#00DA76] hover:text-white font-semibold text-base px-8 py-4 rounded-xl transition-all duration-300 flex items-center justify-center gap-2.5 hover:gap-3 w-full"
                      >
                        <svg className="w-5 h-5 flex-shrink-0" viewBox="0 0 24 24" fill="currentColor">
                          <path d="M12 0C5.373 0 0 5.373 0 12s5.373 12 12 12 12-5.373 12-12S18.627 0 12 0zm5.894 8.221-1.97 9.28c-.145.658-.537.818-1.084.508l-3-2.21-1.447 1.394c-.16.16-.295.295-.605.295l.213-3.053 5.56-5.023c.242-.213-.054-.333-.373-.12l-6.871 4.326-2.962-.924c-.643-.204-.657-.643.136-.953l11.57-4.461c.537-.194 1.006.131.833.941z"/>
                        </svg>
                        <span>Try CLI Demo on Telegram</span>
                        <svg
                          className="w-4 h-4 group-hover:translate-x-0.5 transition-all duration-300"
                          fill="none"
                          stroke="currentColor"
                          viewBox="0 0 24 24"
                        >
                          <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2.5} d="M13 7l5 5m0 0l-5 5m5-5H6" />
                        </svg>
                      </a>
                    </div>

                    <div className="text-center">
                      <p className="text-xs text-gray-500">
                        By accessing TraderBro, you agree to our Terms of
                        Service and Privacy Policy
                      </p>
                    </div>
                  </div>
                </div>
              </div>
            </div>
          </div>

          {/* Right Side - Dashboard Mockup */}
          <div className="relative">
            {/* Demo Label */}
            <div className="text-center mb-6">
              <div
                className="inline-block bg-gradient-to-r from-brand-cta to-emerald-400 text-brand-dark px-6 py-2.5 rounded-full text-sm font-bold shadow-2xl hover:shadow-brand-cta/50 transition-all duration-300 whitespace-nowrap animate-pulse"
                style={{ animationDuration: "2s" }}
              >
                ✨ Explore Dashboard
              </div>
            </div>

            {/* Laptop/Desktop Mockup */}
            <div className="relative mx-auto w-full max-w-5xl overflow-visible">
              {/* Laptop Frame */}
              <div className="relative bg-gradient-to-b from-gray-800 to-gray-900 rounded-t-2xl shadow-2xl p-3 border-4 border-gray-900">
                {/* Screen */}
                <div className="bg-[#0F172A] rounded-xl overflow-hidden shadow-2xl border border-gray-800">
                  {/* Dashboard Header */}
                  <div className="bg-[#1E293B] px-8 py-5 flex items-center justify-between border-b border-gray-700/50">
                    <div className="flex items-center gap-6">
                      <img src={traderbroLogo} alt="TraderBro" className="h-7 w-auto brightness-0 invert" />
                      <div className="flex items-center gap-2.5">
                        <div className="w-2 h-2 bg-green-500 rounded-full animate-pulse shadow-lg shadow-green-500/50"></div>
                        <span className="text-gray-400 text-sm font-medium">Live Market Data</span>
                      </div>
                    </div>
                    <div className="flex items-center gap-6">
                      <div className="text-gray-400 text-sm">
                        Portfolio: <span className="text-white font-bold text-base ml-1">$125,430</span>
                      </div>
                      <div className="w-9 h-9 bg-gradient-to-br from-brand-cta to-emerald-500 rounded-full ring-2 ring-brand-cta/20"></div>
                    </div>
                  </div>

                  <div className="flex h-[520px]">
                    {/* Sidebar */}
                    <div className="w-56 bg-[#1E293B] border-r border-gray-700/50 p-5 flex flex-col">
                      <div className="text-gray-500 text-[10px] font-bold uppercase tracking-wider mb-6">NAVIGATION</div>
                      <div className="space-y-1.5 flex-1">
                        <div className="bg-brand-cta/15 text-brand-cta px-4 py-3 rounded-lg font-medium text-sm flex items-center gap-3 transition-all">
                          <svg className="w-4 h-4 flex-shrink-0" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 19v-6a2 2 0 00-2-2H5a2 2 0 00-2 2v6a2 2 0 002 2h2a2 2 0 002-2zm0 0V9a2 2 0 012-2h2a2 2 0 012 2v10m-6 0a2 2 0 002 2h2a2 2 0 002-2m0 0V5a2 2 0 012-2h2a2 2 0 012 2v14a2 2 0 01-2 2h-2a2 2 0 01-2-2z" />
                          </svg>
                          <span>Analysis</span>
                        </div>
                        <div className="text-gray-400 px-4 py-3 rounded-lg font-medium text-sm flex items-center gap-3 hover:bg-gray-800/50 transition-all cursor-pointer">
                          <svg className="w-4 h-4 flex-shrink-0" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M15 17h5l-1.405-1.405A2.032 2.032 0 0118 14.158V11a6.002 6.002 0 00-4-5.659V5a2 2 0 10-4 0v.341C7.67 6.165 6 8.388 6 11v3.159c0 .538-.214 1.055-.595 1.436L4 17h5m6 0v1a3 3 0 11-6 0v-1m6 0H9" />
                          </svg>
                          <span>Alerts</span>
                        </div>
                        <div className="text-gray-400 px-4 py-3 rounded-lg font-medium text-sm flex items-center gap-3 hover:bg-gray-800/50 transition-all cursor-pointer">
                          <svg className="w-4 h-4 flex-shrink-0" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M21 13.255A23.931 23.931 0 0112 15c-3.183 0-6.22-.62-9-1.745M16 6V4a2 2 0 00-2-2h-4a2 2 0 00-2 2v2m4 6h.01M5 20h14a2 2 0 002-2V8a2 2 0 00-2-2H5a2 2 0 00-2 2v10a2 2 0 002 2z" />
                          </svg>
                          <span>Portfolio</span>
                        </div>
                        <div className="text-gray-400 px-4 py-3 rounded-lg font-medium text-sm flex items-center gap-3 hover:bg-gray-800/50 transition-all cursor-pointer">
                          <svg className="w-4 h-4 flex-shrink-0" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M10.325 4.317c.426-1.756 2.924-1.756 3.35 0a1.724 1.724 0 002.573 1.066c1.543-.94 3.31.826 2.37 2.37a1.724 1.724 0 001.065 2.572c1.756.426 1.756 2.924 0 3.35a1.724 1.724 0 00-1.066 2.573c.94 1.543-.826 3.31-2.37 2.37a1.724 1.724 0 00-2.572 1.065c-.426 1.756-2.924 1.756-3.35 0a1.724 1.724 0 00-2.573-1.066c-1.543.94-3.31-.826-2.37-2.37a1.724 1.724 0 00-1.065-2.572c-1.756-.426-1.756-2.924 0-3.35a1.724 1.724 0 001.066-2.573c-.94-1.543.826-3.31 2.37-2.37.996.608 2.296.07 2.572-1.065z" />
                            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M15 12a3 3 0 11-6 0 3 3 0 016 0z" />
                          </svg>
                          <span>Settings</span>
                        </div>
                      </div>
                    </div>

                    {/* Main Content */}
                    <div className="flex-1 p-5 overflow-y-auto bg-[#0F172A]">
                      {/* Metrics Cards */}
                      <div className="grid grid-cols-3 gap-3 mb-5">
                        {/* Portfolio Value Card */}
                        <div className="bg-gradient-to-br from-[#1E293B] to-[#1a2332] rounded-lg p-3 border border-gray-700/50 shadow-xl hover:shadow-2xl transition-all hover:border-brand-cta/30">
                          <div className="text-gray-400 text-[9px] font-medium uppercase tracking-wider mb-1.5">Portfolio Value</div>
                          <div className="text-white font-bold text-base mb-1 tracking-tight leading-none">$125,430</div>
                          <div className="flex items-center gap-1 text-green-500 text-[10px] font-semibold">
                            <svg className="w-2.5 h-2.5 flex-shrink-0" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={3} d="M13 7h8m0 0v8m0-8l-8 8-4-4-6 6" />
                            </svg>
                            <span>+12.5%</span>
                          </div>
                        </div>

                        {/* Risk Score Card */}
                        <div className="bg-gradient-to-br from-[#1E293B] to-[#1a2332] rounded-lg p-3 border border-gray-700/50 shadow-xl hover:shadow-2xl transition-all hover:border-yellow-500/30">
                          <div className="text-gray-400 text-[9px] font-medium uppercase tracking-wider mb-1.5">Risk Score</div>
                          <div className="text-white font-bold text-base mb-2 tracking-tight leading-none">72/100</div>
                          <div className="w-full bg-gray-700/50 rounded-full h-1.5 overflow-hidden">
                            <div className="bg-gradient-to-r from-yellow-500 to-yellow-400 h-full rounded-full transition-all duration-500" style={{ width: '72%' }}></div>
                          </div>
                        </div>

                        {/* Active Alerts Card */}
                        <div className="bg-gradient-to-br from-[#1E293B] to-[#1a2332] rounded-lg p-3 border border-gray-700/50 shadow-xl hover:shadow-2xl transition-all hover:border-brand-cta/30">
                          <div className="text-gray-400 text-[9px] font-medium uppercase tracking-wider mb-1.5">Active Alerts</div>
                          <div className="text-white font-bold text-base mb-1 tracking-tight leading-none">8</div>
                          <div className="text-brand-cta text-[10px] font-semibold">3 new today</div>
                        </div>
                      </div>

                      {/* Chart Section */}
                      <div className="bg-gradient-to-br from-[#1E293B] to-[#1a2332] rounded-lg p-4 border border-gray-700/50 shadow-xl mb-5">
                        <div className="flex items-center justify-between mb-3">
                          <div className="text-white font-bold text-xs">Portfolio Performance</div>
                          <div className="flex gap-1">
                            <button className="text-[9px] px-2 py-0.5 rounded-md bg-gray-700/50 text-gray-400 hover:bg-gray-700 transition-all font-medium">1D</button>
                            <button className="text-[9px] px-2 py-0.5 rounded-md bg-brand-cta text-white font-bold shadow-md shadow-brand-cta/30">1W</button>
                            <button className="text-[9px] px-2 py-0.5 rounded-md bg-gray-700/50 text-gray-400 hover:bg-gray-700 transition-all font-medium">1M</button>
                            <button className="text-[9px] px-2 py-0.5 rounded-md bg-gray-700/50 text-gray-400 hover:bg-gray-700 transition-all font-medium">1Y</button>
                          </div>
                        </div>
                        <div className="h-32 flex items-end justify-between gap-1 px-0.5">
                          {[65, 70, 68, 75, 72, 80, 85, 82, 88, 90, 87, 92, 95, 93].map((height, i) => (
                            <div
                              key={i}
                              className="flex-1 bg-gradient-to-t from-brand-cta/60 to-brand-cta rounded-t-sm hover:from-brand-cta/80 hover:to-brand-cta transition-all cursor-pointer"
                              style={{
                                height: `${height}%`,
                                animation: `slide-up 0.6s ease-out ${i * 0.05}s backwards`
                              }}
                            ></div>
                          ))}
                        </div>
                      </div>

                      {/* Recent Alerts */}
                      <div className="bg-gradient-to-br from-[#1E293B] to-[#1a2332] rounded-lg p-4 border border-gray-700/50 shadow-xl">
                        <div className="text-white font-bold text-xs mb-2.5">Recent Alerts</div>
                        <div className="space-y-2">
                          <div className="flex items-center justify-between py-1.5 border-b border-gray-700/50 hover:bg-gray-800/30 px-2 -mx-2 rounded transition-all">
                            <div className="flex items-center gap-2 flex-1 min-w-0">
                              <div className="w-1.5 h-1.5 bg-green-500 rounded-full flex-shrink-0 shadow-lg shadow-green-500/50"></div>
                              <span className="text-gray-300 text-[10px] font-medium truncate">AAPL reached target price</span>
                            </div>
                            <span className="text-gray-500 text-[9px] font-medium ml-2 flex-shrink-0">2m ago</span>
                          </div>
                          <div className="flex items-center justify-between py-1.5 border-b border-gray-700/50 hover:bg-gray-800/30 px-2 -mx-2 rounded transition-all">
                            <div className="flex items-center gap-2 flex-1 min-w-0">
                              <div className="w-1.5 h-1.5 bg-yellow-500 rounded-full flex-shrink-0 shadow-lg shadow-yellow-500/50"></div>
                              <span className="text-gray-300 text-[10px] font-medium truncate">TSLA volatility spike</span>
                            </div>
                            <span className="text-gray-500 text-[9px] font-medium ml-2 flex-shrink-0">15m ago</span>
                          </div>
                          <div className="flex items-center justify-between py-1.5 hover:bg-gray-800/30 px-2 -mx-2 rounded transition-all">
                            <div className="flex items-center gap-2 flex-1 min-w-0">
                              <div className="w-1.5 h-1.5 bg-blue-500 rounded-full flex-shrink-0 shadow-lg shadow-blue-500/50"></div>
                              <span className="text-gray-300 text-[10px] font-medium truncate">Portfolio rebalancing</span>
                            </div>
                            <span className="text-gray-500 text-[9px] font-medium ml-2 flex-shrink-0">1h ago</span>
                          </div>
                        </div>
                      </div>
                    </div>
                  </div>
                </div>

                {/* Laptop Camera Notch */}
                <div className="absolute top-0 left-1/2 transform -translate-x-1/2 w-32 h-1.5 bg-gray-950 rounded-b-full shadow-inner"></div>
              </div>

              {/* Laptop Base */}
              <div className="relative h-5">
                <div className="absolute inset-x-0 bottom-0 h-5 bg-gradient-to-b from-gray-700 via-gray-800 to-gray-900 rounded-b-2xl shadow-2xl"></div>
                <div className="absolute inset-x-1/4 bottom-0 h-2.5 bg-gray-950 rounded-t-xl shadow-inner"></div>
              </div>

              {/* Enhanced Glow Effect */}
              <div
                className="absolute -inset-10 bg-gradient-to-r from-brand-cta/25 via-emerald-500/20 to-teal-500/15 blur-3xl -z-10 opacity-60 animate-pulse"
                style={{ animationDuration: "4s" }}
              ></div>
              <div className="absolute -inset-14 bg-gradient-to-br from-brand-cta/10 to-transparent blur-3xl -z-20"></div>
            </div>
          </div>
        </div>
      </div>

      {/* Additional Sections from Landing Page */}
      <div className="relative z-10 overflow-x-hidden">
        <LiveActivityFeed />
        <CoreFeatures />

        {/* CLI Demo Section */}
        <section id="cli-demo" className="bg-[#0F172A] py-16 md:py-20 relative overflow-hidden">
          {/* Background glow */}
          <div className="absolute inset-0 pointer-events-none">
            <div className="absolute w-[600px] h-[600px] bg-[#00DA76]/6 rounded-full blur-3xl -top-48 -left-48"></div>
            <div className="absolute w-[400px] h-[400px] bg-emerald-500/5 rounded-full blur-3xl bottom-0 right-0"></div>
          </div>

          <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 relative z-10">
            <div className="grid grid-cols-1 lg:grid-cols-2 gap-12 items-center">
              {/* Left — Text */}
              <div>
                <div className="inline-flex items-center gap-2 bg-[#00DA76]/10 border border-[#00DA76]/20 text-[#00DA76] text-sm font-semibold px-4 py-2 rounded-full mb-6">
                  <svg className="w-4 h-4 flex-shrink-0" viewBox="0 0 24 24" fill="currentColor">
                    <path d="M12 0C5.373 0 0 5.373 0 12s5.373 12 12 12 12-5.373 12-12S18.627 0 12 0zm5.894 8.221-1.97 9.28c-.145.658-.537.818-1.084.508l-3-2.21-1.447 1.394c-.16.16-.295.295-.605.295l.213-3.053 5.56-5.023c.242-.213-.054-.333-.373-.12l-6.871 4.326-2.962-.924c-.643-.204-.657-.643.136-.953l11.57-4.461c.537-.194 1.006.131.833.941z"/>
                  </svg>
                  <span>Live on Telegram</span>
                </div>

                <h2 className="text-4xl sm:text-5xl font-bold text-white mb-5 leading-tight tracking-tight">
                  TraderBro CLI<br />
                  <span className="text-[#00DA76]">AI Agent</span> in Your Terminal
                </h2>

                <p className="text-gray-400 text-lg leading-relaxed mb-8 font-light">
                  Chat directly with our autonomous AI trading agent on Telegram. Ask for charts, screen stocks, get real-time analysis — no dashboard needed.
                </p>

                <ul className="space-y-3 mb-8">
                  {[
                    "Natural language stock queries",
                    "Instant chart generation",
                    "AI-powered market screening",
                    "Zero setup — open Telegram and start",
                  ].map((item) => (
                    <li key={item} className="flex items-center gap-3 text-gray-300 text-sm">
                      <span className="w-5 h-5 bg-[#00DA76]/15 rounded-full flex items-center justify-center flex-shrink-0">
                        <svg className="w-3 h-3 text-[#00DA76]" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                          <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={3} d="M5 13l4 4L19 7" />
                        </svg>
                      </span>
                      {item}
                    </li>
                  ))}
                </ul>

                {/* Compatible AI agents */}
                <div className="mb-10">
                  <p className="text-gray-500 text-xs font-semibold uppercase tracking-widest mb-4">
                    Works with any AI agent you self-host
                  </p>
                  <div className="flex flex-wrap gap-3">
                    {/* Claude */}
                    <div className="flex items-center gap-2 bg-[#1E293B] border border-gray-700/60 rounded-full px-3 py-1.5 hover:border-[#CC785C]/50 transition-colors">
                      <span className="w-5 h-5 rounded-full bg-[#CC785C] flex items-center justify-center flex-shrink-0">
                        <svg viewBox="0 0 24 24" className="w-3 h-3 fill-white" xmlns="http://www.w3.org/2000/svg">
                          <path d="M13.827 3.41c-.892-1.817-3.447-1.817-4.34 0L2.65 16.56C1.818 18.26 3.054 20.25 4.97 20.25h14.747c1.916 0 3.152-1.99 2.322-3.69L13.827 3.41zm-2.17 1.006a.75.75 0 011.342 0l7.188 14.627a.75.75 0 01-.671 1.082H4.97a.75.75 0 01-.67-1.082l7.357-14.627z"/>
                        </svg>
                      </span>
                      <span className="text-gray-300 text-xs font-medium">Claude</span>
                    </div>

                    {/* ChatGPT */}
                    <div className="flex items-center gap-2 bg-[#1E293B] border border-gray-700/60 rounded-full px-3 py-1.5 hover:border-[#10A37F]/50 transition-colors">
                      <span className="w-5 h-5 rounded-full bg-[#10A37F] flex items-center justify-center flex-shrink-0">
                        <svg viewBox="0 0 24 24" className="w-3 h-3 fill-white" xmlns="http://www.w3.org/2000/svg">
                          <path d="M22.282 9.821a5.985 5.985 0 00-.516-4.91 6.046 6.046 0 00-6.51-2.9A6.065 6.065 0 004.981 4.18a5.985 5.985 0 00-3.998 2.9 6.046 6.046 0 00.743 7.097 5.98 5.98 0 00.51 4.911 6.051 6.051 0 006.515 2.9A5.985 5.985 0 0013.26 24a6.056 6.056 0 005.772-4.206 5.99 5.99 0 003.997-2.9 6.056 6.056 0 00-.747-7.073zM13.26 22.43a4.476 4.476 0 01-2.876-1.04l.141-.081 4.779-2.758a.795.795 0 00.392-.681v-6.737l2.02 1.168a.071.071 0 01.038.052v5.583a4.504 4.504 0 01-4.494 4.494zM3.6 18.304a4.47 4.47 0 01-.535-3.014l.142.085 4.783 2.759a.771.771 0 00.78 0l5.843-3.369v2.332a.08.08 0 01-.032.067L9.73 19.95a4.5 4.5 0 01-6.13-1.646zM2.34 7.896a4.485 4.485 0 012.366-1.973V11.6a.766.766 0 00.388.676l5.815 3.355-2.02 1.168a.076.076 0 01-.071 0l-4.83-2.786A4.504 4.504 0 012.34 7.872zm16.597 3.855l-5.833-3.387L15.119 7.2a.076.076 0 01.071 0l4.83 2.791a4.494 4.494 0 01-.676 8.105v-5.678a.79.79 0 00-.407-.667zm2.01-3.023l-.141-.085-4.774-2.782a.776.776 0 00-.785 0L9.409 9.23V6.897a.066.066 0 01.028-.061l4.83-2.787a4.5 4.5 0 016.68 4.66zm-12.64 4.135l-2.02-1.164a.08.08 0 01-.038-.057V6.075a4.5 4.5 0 017.375-3.453l-.142.08L8.704 5.46a.795.795 0 00-.393.681zm1.097-2.365l2.602-1.5 2.607 1.5v2.999l-2.597 1.5-2.607-1.5z"/>
                        </svg>
                      </span>
                      <span className="text-gray-300 text-xs font-medium">ChatGPT</span>
                    </div>

                    {/* Hermes */}
                    <div className="flex items-center gap-2 bg-[#1E293B] border border-gray-700/60 rounded-full px-3 py-1.5 hover:border-[#7C3AED]/50 transition-colors">
                      <span className="w-5 h-5 rounded-full bg-[#7C3AED] flex items-center justify-center flex-shrink-0 text-white text-[9px] font-bold">{AGENT_NAME.charAt(0)}</span>
                      <span className="text-gray-300 text-xs font-medium">{AGENT_NAME}</span>
                    </div>

                    {/* Openclaw */}
                    <div className="flex items-center gap-2 bg-[#1E293B] border border-gray-700/60 rounded-full px-3 py-1.5 hover:border-[#2563EB]/50 transition-colors">
                      <span className="w-5 h-5 rounded-full bg-[#2563EB] flex items-center justify-center flex-shrink-0">
                        <svg viewBox="0 0 24 24" className="w-3 h-3 fill-white" xmlns="http://www.w3.org/2000/svg">
                          <path d="M12 2C6.48 2 2 6.48 2 12s4.48 10 10 10 10-4.48 10-10S17.52 2 12 2zm-1 14H9V8h2v8zm4 0h-2V8h2v8z"/>
                        </svg>
                      </span>
                      <span className="text-gray-300 text-xs font-medium">Openclaw</span>
                    </div>

                    {/* Any model */}
                    <div className="flex items-center gap-2 bg-[#00DA76]/8 border border-[#00DA76]/25 rounded-full px-3 py-1.5">
                      <span className="text-[#00DA76] text-xs">+</span>
                      <span className="text-[#00DA76] text-xs font-medium">Any OpenAI-compatible API</span>
                    </div>
                  </div>
                </div>

                <a
                  href="https://t.me/traderbro_cli_bot"
                  target="_blank"
                  rel="noopener noreferrer"
                  onClick={() => {
                    if (typeof gtag !== "undefined") {
                      gtag("event", "click", {
                        event_category: "cli_cta",
                        event_label: "cli_section_telegram_button",
                        value: 1,
                      });
                    }
                  }}
                  className="group inline-flex items-center gap-3 bg-[#00DA76] hover:bg-emerald-400 text-[#0F172A] font-bold text-base px-8 py-4 rounded-xl transition-all duration-300 shadow-lg shadow-[#00DA76]/25 hover:shadow-[#00DA76]/40 hover:gap-3.5"
                >
                  <svg className="w-5 h-5 flex-shrink-0" viewBox="0 0 24 24" fill="currentColor">
                    <path d="M12 0C5.373 0 0 5.373 0 12s5.373 12 12 12 12-5.373 12-12S18.627 0 12 0zm5.894 8.221-1.97 9.28c-.145.658-.537.818-1.084.508l-3-2.21-1.447 1.394c-.16.16-.295.295-.605.295l.213-3.053 5.56-5.023c.242-.213-.054-.333-.373-.12l-6.871 4.326-2.962-.924c-.643-.204-.657-.643.136-.953l11.57-4.461c.537-.194 1.006.131.833.941z"/>
                  </svg>
                  <span>Open Demo Bot</span>
                  <svg className="w-4 h-4 group-hover:translate-x-0.5 transition-all duration-300" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2.5} d="M13 7l5 5m0 0l-5 5m5-5H6" />
                  </svg>
                </a>
              </div>

              {/* Right — Terminal mockup */}
              <div className="relative">
                <div className="bg-[#1E293B] rounded-2xl border border-gray-700/60 shadow-2xl overflow-hidden">
                  {/* Terminal title bar */}
                  <div className="flex items-center gap-2 px-4 py-3 bg-[#0d1829] border-b border-gray-700/50">
                    <div className="w-3 h-3 rounded-full bg-red-500/70"></div>
                    <div className="w-3 h-3 rounded-full bg-yellow-500/70"></div>
                    <div className="w-3 h-3 rounded-full bg-green-500/70"></div>
                    <span className="ml-3 text-gray-500 text-xs font-mono">traderbro-cli — AI Agent</span>
                  </div>

                  {/* Terminal content */}
                  <div className="p-6 font-mono text-sm space-y-3">
                    <div className="flex gap-3">
                      <span className="text-gray-500 flex-shrink-0">You</span>
                      <span className="text-white">Show me AAPL chart with RSI</span>
                    </div>
                    <div className="flex gap-3">
                      <span className="text-[#00DA76] flex-shrink-0">AI</span>
                      <span className="text-gray-300">Fetching AAPL data from market feed...</span>
                    </div>
                    <div className="flex gap-3">
                      <span className="text-[#00DA76] flex-shrink-0">AI</span>
                      <span className="text-gray-300">Running technical analysis engine...</span>
                    </div>
                    <div className="bg-[#0F172A] rounded-lg p-4 border border-[#00DA76]/20 mt-2">
                      <div className="text-[#00DA76] font-semibold mb-2">AAPL — 1Y Chart + RSI Analysis</div>
                      <div className="text-gray-400 text-xs leading-relaxed">
                        Trend: <span className="text-green-400 font-medium">Bullish</span> · RSI: <span className="text-yellow-400 font-medium">68 (near overbought)</span><br />
                        Support: $220 · Resistance: $260<br />
                        Signal: <span className="text-[#00DA76] font-medium">Watch for consolidation near $260</span>
                      </div>
                    </div>
                    <div className="flex gap-3">
                      <span className="text-gray-500 flex-shrink-0">You</span>
                      <span className="text-white">Screen top momentum stocks today</span>
                    </div>
                    <div className="flex gap-3">
                      <span className="text-[#00DA76] flex-shrink-0">AI</span>
                      <span className="text-gray-300">Scanning 8,000+ stocks across all sectors<span className="animate-pulse">_</span></span>
                    </div>
                  </div>
                </div>

                {/* Glow under terminal */}
                <div className="absolute -inset-4 bg-[#00DA76]/8 blur-2xl -z-10 rounded-3xl"></div>
              </div>
            </div>
          </div>
        </section>

        {/* Technology Section */}
        <section id="technology" className="bg-gradient-to-b from-gray-50 to-white py-12 md:py-16 relative">
          <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
            {/* Section Header */}
            <div className="text-center mb-10 md:mb-12">
              <h2 className="text-4xl sm:text-5xl md:text-6xl font-bold text-[#222425] mb-6 tracking-tight">
                Powered by Proprietary Technology
              </h2>
              <p className="text-lg md:text-xl text-gray-600 max-w-3xl mx-auto font-light">
                Custom Agentic Workflows & Advanced Data Processing
              </p>
            </div>

            {/* Technology Grid */}
            <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-6 lg:gap-8">
              {/* Tech Feature 1 */}
              <div className="bg-white rounded-2xl p-6 border border-gray-100 hover:border-[#00DA76]/30 hover:shadow-lg transition-all duration-300">
                <div className="w-12 h-12 bg-gradient-to-br from-[#00DA76]/10 to-emerald-500/10 rounded-xl flex items-center justify-center mb-4">
                  <svg className="w-6 h-6 text-[#00DA76]" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9.663 17h4.673M12 3v1m6.364 1.636l-.707.707M21 12h-1M4 12H3m3.343-5.657l-.707-.707m2.828 9.9a5 5 0 117.072 0l-.548.547A3.374 3.374 0 0014 18.469V19a2 2 0 11-4 0v-.531c0-.895-.356-1.754-.988-2.386l-.548-.547z" />
                  </svg>
                </div>
                <h3 className="text-xl font-bold text-[#222425] mb-3">LangGraph-based Agent Orchestration</h3>
                <p className="text-gray-600 text-sm leading-relaxed">
                  Advanced AI agent coordination for complex financial analysis workflows
                </p>
              </div>

              {/* Tech Feature 2 */}
              <div className="bg-white rounded-2xl p-6 border border-gray-100 hover:border-[#00DA76]/30 hover:shadow-lg transition-all duration-300">
                <div className="w-12 h-12 bg-gradient-to-br from-[#00DA76]/10 to-emerald-500/10 rounded-xl flex items-center justify-center mb-4">
                  <svg className="w-6 h-6 text-[#00DA76]" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M19.428 15.428a2 2 0 00-1.022-.547l-2.387-.477a6 6 0 00-3.86.517l-.318.158a6 6 0 01-3.86.517L6.05 15.21a2 2 0 00-1.806.547M8 4h8l-1 1v5.172a2 2 0 00.586 1.414l5 5c1.26 1.26.367 3.414-1.415 3.414H4.828c-1.782 0-2.674-2.154-1.414-3.414l5-5A2 2 0 009 10.172V5L8 4z" />
                  </svg>
                </div>
                <h3 className="text-xl font-bold text-[#222425] mb-3">Proprietary Market Data Processing</h3>
                <p className="text-gray-600 text-sm leading-relaxed">
                  Custom algorithms process millions of market signals in real-time
                </p>
              </div>

              {/* Tech Feature 3 */}
              <div className="bg-white rounded-2xl p-6 border border-gray-100 hover:border-[#00DA76]/30 hover:shadow-lg transition-all duration-300">
                <div className="w-12 h-12 bg-gradient-to-br from-[#00DA76]/10 to-emerald-500/10 rounded-xl flex items-center justify-center mb-4">
                  <svg className="w-6 h-6 text-[#00DA76]" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M13 10V3L4 14h7v7l9-11h-7z" />
                  </svg>
                </div>
                <h3 className="text-xl font-bold text-[#222425] mb-3">Sub-Second Analysis Engine</h3>
                <p className="text-gray-600 text-sm leading-relaxed">
                  Real-time analysis with institutional-grade latency and accuracy
                </p>
              </div>

              {/* Tech Feature 4 */}
              <div className="bg-white rounded-2xl p-6 border border-gray-100 hover:border-[#00DA76]/30 hover:shadow-lg transition-all duration-300">
                <div className="w-12 h-12 bg-gradient-to-br from-[#00DA76]/10 to-emerald-500/10 rounded-xl flex items-center justify-center mb-4">
                  <svg className="w-6 h-6 text-[#00DA76]" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M15 17h5l-1.405-1.405A2.032 2.032 0 0118 14.158V11a6.002 6.002 0 00-4-5.659V5a2 2 0 10-4 0v.341C7.67 6.165 6 8.388 6 11v3.159c0 .538-.214 1.055-.595 1.436L4 17h5m6 0v1a3 3 0 11-6 0v-1m6 0H9" />
                  </svg>
                </div>
                <h3 className="text-xl font-bold text-[#222425] mb-3">Custom Notification Infrastructure</h3>
                <p className="text-gray-600 text-sm leading-relaxed">
                  Multi-channel delivery system with intelligent routing and prioritization
                </p>
              </div>
            </div>

            {/* Technical Details */}
            <div className="mt-12 bg-gradient-to-br from-[#00DA76]/5 to-emerald-400/5 rounded-3xl p-8 md:p-10 border border-[#00DA76]/20">
              <div className="text-center mb-8">
                <h3 className="text-2xl md:text-3xl font-bold text-[#222425] mb-3">
                  Enterprise-Grade Infrastructure
                </h3>
                <p className="text-gray-600 max-w-2xl mx-auto">
                  Built on cutting-edge technology stack for reliability, scalability, and performance
                </p>
              </div>
              <div className="grid grid-cols-2 md:grid-cols-4 gap-6 text-center">
                <div>
                  <div className="text-3xl font-bold text-[#00DA76] mb-2">99.9%</div>
                  <div className="text-sm text-gray-600">Uptime SLA</div>
                </div>
                <div>
                  <div className="text-3xl font-bold text-[#00DA76] mb-2">&lt;100ms</div>
                  <div className="text-sm text-gray-600">API Response Time</div>
                </div>
                <div>
                  <div className="text-3xl font-bold text-[#00DA76] mb-2">10M+</div>
                  <div className="text-sm text-gray-600">Data Points/Day</div>
                </div>
                <div>
                  <div className="text-3xl font-bold text-[#00DA76] mb-2">24/7</div>
                  <div className="text-sm text-gray-600">Market Monitoring</div>
                </div>
              </div>
            </div>
          </div>
        </section>

        <AboutUs />
        <PricingPage />
      </div>
      <Footer />
    </div>
  );
};

export default LandingPage;
